import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';

import { Icon } from '@components/icon';
import './Toast.scss';
import { Heading } from '@components/text';

export interface ToastProps {
  /** Aria-label for dismiss (X) icon button */
  dismissLabel?: string;
  /** Duration in Milliseconds */
  duration?: number;
  /** Message shown on Toast */
  message?: string;
  /** Callback on close */
  onClose?: () => void;
  /** Target Element ID */
  targetId?: string;
  /** Toast variant */
  variant?: 'primary' | 'secondary';
}

/**
 * The Toast component displays floating content in relation to a target element,
 * typically used to display additional information.
 * <p>Toast shows when <em><strong>message</strong></em> property state changes.
 * </p>
 * @param dismissLabel - aria-label for dismiss (X) icon button
 * @param duration - in milliseconds. default 5000
 * @param message - message to show
 * @param onClose - callback on close
 * @param targetId - target element ID
 * @param variant - style variant. default 'primary'
 * @constructor
 */
export const Toast: React.FC<ToastProps> = ({
  dismissLabel,
  duration = 5000,
  message,
  onClose,
  targetId,
  variant = 'primary',
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const [toastTarget, setToastTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (targetId) {
      setToastTarget(document.getElementById(targetId));
    }
  }, [targetId]);

  useEffect(() => {
    setIsVisible(true);
    const timer = setTimeout(() => {
      setIsVisible(false);
      if (onClose) {
        onClose();
      }
    }, duration);
    return () => clearTimeout(timer);
    // disabling exhaustive-deps in case parent doesnt memoize onClose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, duration]);

  const closeToastHandler = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const toastContent = (
    <AnimatePresence>
      {isVisible && message && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className={classNames('vds-toast', `vds-toast--${variant}`)}
          role="status"
          aria-live="polite">
          <Heading
            lineClamp
            variant="heading-03"
            size="small"
            weight="bold"
            className="vds-toast__heading">
            {message}
          </Heading>
          <button
            aria-label={dismissLabel}
            onClick={closeToastHandler}
            className="vds-toast__close-button">
            <Icon name="close" size="small" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
  return toastTarget ? createPortal(toastContent, toastTarget) : toastContent;
};
